import * as THREE from 'three/webgpu';
import {
    createSketchCloneContext,
    isAbstractSketchEdgeObject,
    isSketchCollectionObject,
    isSketchPointObject,
    type CollectSource,
    type SketchGroup,
    type SketchPointObject,
} from '../../mesh/sketch';

/**
 * 集合子边移动拆分上下文。
 */
export interface SketchCollectionChildMoveDetachContext {
    /** 拆分前的原始选择实体。 */
    readonly originalSelectedEntities: readonly THREE.Object3D[];
    /** 拆分后的实际移动实体。 */
    readonly detachedSelectedEntities: readonly THREE.Object3D[];
    /** 提交拆分结果。 */
    commit(): void;
    /** 回滚拆分结果。 */
    rollback(): void;
}

/**
 * 集合子边移动拆分计划。
 */
interface SketchCollectionChildMoveDetachPlan {
    /** 源闭合集合。 */
    collection: THREE.Object3D & {
        collectSet: Set<CollectSource>;
        setSources: (sources: CollectSource[]) => void;
        syncContour: () => void;
        canCreateContour: () => boolean;
        clearContourDisplay: () => void;
    };
    /** 原始集合源边顺序快照。 */
    originalSources: CollectSource[];
    /** 本次要拆分的选中源边。 */
    selectedSources: CollectSource[];
    /** 拆分后保留在原集合中的源边。 */
    remainingSources: CollectSource[];
}

/**
 * 草图闭合集合子边移动拆分服务。
 *
 * 设计说明：
 * 1. Move Entities 选中闭合集合中的单条线/圆弧时，不能继续沿用原集合共享端点；
 * 2. 这里通过“克隆被选边 + 原集合移除被选边”实现拓扑拆分；
 * 3. 使用同一个 clone context 可保证被选子边之间继续共享端点，而与未选边断开共享。
 */
export class SketchCollectionChildMoveDetachService {
    /**
     * 为移动实体命令拆分闭合集合子边。
     *
     * @param entities 当前选择实体。
     * @param sketchGroup 活动草图组。
     * @returns 拆分上下文；无须拆分时返回 null。
     */
    public detachCollectionChildEntitiesForMove(
        entities: readonly THREE.Object3D[],
        sketchGroup: SketchGroup,
    ): SketchCollectionChildMoveDetachContext | null {
        const plans = this.collectDetachPlans(entities);
        if (plans.length === 0) {
            return null;
        }

        const replacementMap = new Map<string, THREE.Object3D>();
        const rollbackTasks: Array<() => void> = [];

        plans.forEach((plan) => {
            const cloneContext = createSketchCloneContext();
            const detachedClones = plan.selectedSources.map((source) => source.cloneSelf(cloneContext)) as CollectSource[];

            plan.collection.setSources(plan.remainingSources);
            this.syncCollectionContour(plan.collection);

            detachedClones.forEach((clone, index) => {
                sketchGroup.add(clone);
                replacementMap.set(plan.selectedSources[index].uuid, clone);
            });
            const attachedRelatedPoints = this.attachDetachedRelatedPoints(detachedClones, sketchGroup);

            rollbackTasks.push(() => {
                detachedClones.forEach((clone) => {
                    if (clone.parent) {
                        clone.parent.remove(clone);
                    }
                });
                attachedRelatedPoints.forEach((point) => {
                    // 只回收本轮亲自挂载且仍直属当前草图组的点，避免误删后续流程重新归属的对象。
                    if (point.parent === sketchGroup) {
                        sketchGroup.remove(point);
                    }
                });
                plan.collection.setSources(plan.originalSources);
                this.syncCollectionContour(plan.collection);
            });
        });

        return {
            originalSelectedEntities: [...entities],
            detachedSelectedEntities: entities.map((entity) => replacementMap.get(entity.uuid) ?? entity),
            commit(): void {
                rollbackTasks.length = 0;
            },
            rollback(): void {
                rollbackTasks.slice().reverse().forEach((rollbackTask) => {
                    rollbackTask();
                });
                rollbackTasks.length = 0;
            },
        };
    }

    /**
     * 收集需要拆分的闭合集合子边计划。
     *
     * @param entities 当前选择实体。
     * @returns 拆分计划列表。
     */
    private collectDetachPlans(entities: readonly THREE.Object3D[]): SketchCollectionChildMoveDetachPlan[] {
        const planMap = new Map<string, SketchCollectionChildMoveDetachPlan>();

        entities.forEach((entity) => {
            if (!isAbstractSketchEdgeObject(entity)) {
                return;
            }

            const closedCollection = Reflect.get(entity, 'closedCollection') as unknown;
            if (!isSketchCollectionObject(closedCollection)) {
                return;
            }

            const collectionSources = Array.from(closedCollection.collectSet);
            const sourceEntity = entity as CollectSource;
            if (!collectionSources.includes(sourceEntity)) {
                return;
            }

            const existingPlan = planMap.get(closedCollection.uuid);
            if (existingPlan) {
                if (!existingPlan.selectedSources.includes(sourceEntity)) {
                    existingPlan.selectedSources.push(sourceEntity);
                    existingPlan.remainingSources = existingPlan.originalSources.filter((source) => !existingPlan.selectedSources.includes(source));
                }
                return;
            }

            planMap.set(closedCollection.uuid, {
                collection: closedCollection,
                originalSources: collectionSources,
                selectedSources: [sourceEntity],
                remainingSources: collectionSources.filter((source) => source !== sourceEntity),
            });
        });

        return Array.from(planMap.values()).filter((plan) => (
            plan.selectedSources.length > 0 && plan.selectedSources.length < plan.originalSources.length
        ));
    }

    /**
     * 同步集合轮廓显示。
     *
     * @param collection 目标集合。
     */
    private syncCollectionContour(planCollection: SketchCollectionChildMoveDetachPlan['collection']): void {
        if (planCollection.canCreateContour()) {
            planCollection.syncContour();
            return;
        }
        planCollection.clearContourDisplay();
    }

    /**
     * 将拆分副本中尚未挂载到对象树的关联点补挂到草图组。
     *
     * 设计说明：
     * 1. 线/圆弧 cloneSelf 会创建新的定义控制点，但这些点未必都是边对象 child；
     * 2. Move 子边拆分后若不补挂，命中的独立边会出现控制点没有 parent 的悬空状态；
     * 3. 全部副本先完成挂载，再批量按 UUID 去重关联点，保证相邻多边同时拆分时共享点只挂载一次；
     * 4. 返回值只包含本轮实际添加到草图组的点，供回滚与取消路径精确回收。
     *
     * @param detachedClones 当前拆分域的全部副本。
     * @param sketchGroup 活动草图组。
     * @returns 本轮实际挂载到草图组的关联点。
     */
    private attachDetachedRelatedPoints(
        detachedClones: readonly CollectSource[],
        sketchGroup: SketchGroup,
    ): SketchPointObject[] {
        const detachedPointMap = new Map<string, SketchPointObject>();
        detachedClones.forEach((clone) => {
            clone.relatedSketchPoints.forEach((point) => {
                if (isSketchPointObject(point) && !point.parent) {
                    detachedPointMap.set(point.uuid, point);
                }
            });
        });

        const attachedPoints = Array.from(detachedPointMap.values());
        attachedPoints.forEach((point) => {
            sketchGroup.add(point);
        });
        return attachedPoints;
    }
}
